import React, { useState, useEffect } from "react";
import "./scorebox.css";
import "./SpellTheWord.css";
import InfoTooltip from "../../../InfoTooltip";
import TipButton from "../../../TipButton";
import StudentInfoTooltip from "../../../StudentInfoTooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLeftRight } from "@fortawesome/free-solid-svg-icons";
import { handleScoreChange } from "./helpers/scoreHelpers";
import CategoryTags from "./CategoryTags";
const SpellTheWord = ({
  sentence = "",
  onChange,
  border,
  score,
  isStudentView = false,
  studentResponse,
  tip,
  questionCategories = [],
  questionId,
  showCategories = true,
  onFlip,
}) => {
  const [words, setWords] = useState([]);
  const [letters, setLetters] = useState([]);
  const [filledLetters, setFilledLetters] = useState([]);
  const [isCorrect, setIsCorrect] = useState(null);
  const [initialLetters, setInitialLetters] = useState([]);

  const onScoreChange = handleScoreChange(onChange);

  useEffect(() => {
    if (sentence) {
      const parsedWords = sentence.match(/\[.*?\]/g) || [];
      const wordsWithoutBrackets = parsedWords.map((word) => word.slice(1, -1));
      setWords(wordsWithoutBrackets);

      const allLetters = wordsWithoutBrackets.flatMap((word, wordIndex) =>
        word.split("").map((letter, letterIndex) => ({
          id: `letter-${wordIndex}-${letterIndex}`,
          content: letter,
          wordIndex: wordIndex,
        }))
      );

      const shuffledLetters = [...allLetters].sort(() => Math.random() - 0.5);

      setLetters(shuffledLetters);
      setInitialLetters(shuffledLetters);
      setFilledLetters(Array(shuffledLetters.length).fill(null));
    }
  }, [sentence]);

  useEffect(() => {
    if (
      isStudentView &&
      studentResponse &&
      typeof studentResponse.answer === "string" &&
      initialLetters.length > 0
    ) {
      // Create new arrays instead of modifying state directly
      let newFilledLetters = Array(initialLetters.length).fill(null);
      let remainingLetters = [...initialLetters];

      // For each letter in the student's answer, find a matching letter and place it
      studentResponse.answer.split("").forEach((answerLetter) => {
        const letterIndex = remainingLetters.findIndex(
          (l) => l.content.toLowerCase() === answerLetter.toLowerCase()
        );

        if (letterIndex !== -1) {
          const letter = remainingLetters[letterIndex];
          const nextEmptyIndex = newFilledLetters.findIndex((l) => l === null);

          if (nextEmptyIndex !== -1) {
            newFilledLetters[nextEmptyIndex] = letter;
            remainingLetters.splice(letterIndex, 1);
          }
        }
      });

      // Set both states at once
      setFilledLetters(newFilledLetters);
      setLetters(remainingLetters);
    }
  }, [isStudentView, studentResponse, initialLetters]);

  const handleLetterClick = (letter) => {
    const nextEmptyIndex = filledLetters.findIndex((l) => l === null);
    if (nextEmptyIndex !== -1) {
      const newFilledLetters = [...filledLetters];
      newFilledLetters[nextEmptyIndex] = letter;

      const newLetters = letters.filter((l) => l.id !== letter.id);

      const answerString = newFilledLetters
        .map((letter) => (letter ? letter.content : ""))
        .join("");

      setFilledLetters(newFilledLetters);
      setLetters(newLetters);

      if (isStudentView) {
        onChange({
          type: "SpellTheWord",
          answer: answerString,
        });
      }
    }
  };

  const handleSentenceChange = (e) => {
    const newSentence = e.target.value;
    const bracketedWords = newSentence.match(/\[.*?\]/g) || [];

    if (bracketedWords.length > 1) {
      alert("Please enter only one word in brackets.");
      return;
    }

    const singleWordInBrackets = bracketedWords[0]?.match(/\[(\w+)\]/) !== null;

    if (!singleWordInBrackets && bracketedWords.length > 0) {
      alert("Please enter only a single word within brackets.");
      return;
    }

    onChange({
      sentence: newSentence,
      correctAnswers: [
        bracketedWords.map((word) => word.slice(1, -1)).join(""),
      ],
      questionCategories: ["grammar", "spelling"],
    });
  };

  const checkSpelling = () => {
    const currentSpelling = filledLetters
      .map((letter) => letter?.content || "")
      .join("");
    const correctSpelling = words.join("");
    const isCorrectSpelling = currentSpelling === correctSpelling;
    setIsCorrect(isCorrectSpelling);
  };

  const handleTryAgain = () => {
    const shuffled = [...initialLetters].sort(() => Math.random() - 0.5);
    setLetters(shuffled);
    setFilledLetters(Array(initialLetters.length).fill(null));
    setIsCorrect(null);
    if (isStudentView) {
      onChange({
        type: "SpellTheWord",
        answer: "",
      });
    }
  };

  const renderSentence = () => {
    if (!sentence) return null;

    const parts = sentence.split(/(\[.*?\])/g);
    let currentIndex = 0;
    return parts.map((part, partIndex) => {
      if (part.startsWith("[") && part.endsWith("]")) {
        const word = part.slice(1, -1);
        return (
          <span key={partIndex} className="spell-word-blank">
            {word.split("").map((_, letterIndex) => {
              const index = currentIndex++;
              return (
                <span key={letterIndex} className="letter-slot">
                  {filledLetters[index]?.content || "_"}
                </span>
              );
            })}
          </span>
        );
      }
      return <span key={partIndex}>{part}</span>;
    });
  };

  return (
    <div className={`question-container ${border}`}>
      <div className="question-header">
        {isStudentView ? (
          <>
            <div className="audio-recorder-question">
              Click on the letter pieces to fill in the blanks and make the
              sentence meaningful.
            </div>
            <StudentInfoTooltip type="Spell the Word" />
          </>
        ) : (
          <>
            <input
              type="text"
              value={sentence}
              onChange={handleSentenceChange}
              placeholder="Enter your sentence with [words] to spell in brackets"
              className="question-input"
            />
            <InfoTooltip type="Spell the Word" />
          </>
        )}
      </div>
      <div className="sentence-display">{renderSentence()}</div>
      <div className="letters-container">
        {letters.map((letter) => (
          <div
            key={letter.id}
            onClick={() => handleLetterClick(letter)}
            className="letter-piece"
          >
            {letter.content}
          </div>
        ))}
      </div>
      <div className="category-tags-container">
        <CategoryTags
          categories={questionCategories}
          onChange={(newCategories) =>
            onChange({
              questionCategories: newCategories,
            })
          }
          isStudentView={isStudentView}
          showCategories={showCategories}
        />
      </div>
      <div className="question-footer">
        <div className="question-footer-flex">
          <div className="button-wrapper">
            {!isStudentView && (
              <button
                className="question-footer-button"
                onClick={checkSpelling}
              >
                Check
              </button>
            )}
            <button className="question-footer-button" onClick={handleTryAgain}>
              Shuffle
            </button>
          </div>
          <TipButton
            tip={tip}
            onChange={onChange}
            isStudentView={isStudentView}
            onFlip={onFlip}
          />
          <div className="scorebox-container">
            <label htmlFor="score">Score:</label>
            <input
              type="number"
              id="score"
              value={score}
              onChange={onScoreChange}
              min="0"
              step="1"
              readOnly={isStudentView}
              className={isStudentView ? "student-view" : ""}
            />
          </div>
        </div>
      </div>
      {!isStudentView && isCorrect !== null && (
        <p>
          {isCorrect ? "Correct spelling!" : "Incorrect spelling. Try again!"}
        </p>
      )}
    </div>
  );
};

export default SpellTheWord;
